import { useState } from "react";
import type { InsightsTimeseriesPoint, InsightsRange } from "@/lib/api";

const CHART_W = 600;
const CHART_H = 180;
const PAD_LEFT = 52;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 36;

const INNER_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const INNER_H = CHART_H - PAD_TOP - PAD_BOTTOM;

function formatXLabel(iso: string, range: InsightsRange): string {
  const d = new Date(iso);
  if (range === "24h") {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

interface TooltipData {
  x: number;
  y: number;
  point: InsightsTimeseriesPoint;
  formattedValue: string;
}

interface InsightsLineChartProps {
  title: string;
  points: InsightsTimeseriesPoint[];
  range: InsightsRange;
  formatValue?: (v: number) => string;
  color?: string;
  /**
   * Como tratar pontos imputados (hora sem dado real):
   * - "gap" (padrão): ficam fora da linha e da escala — o gráfico mostra um
   *   buraco em vez de mergulhar a um zero que nunca aconteceu.
   * - "carry": entram na linha com o valor herdado do backend (métricas de
   *   storage, em que o último valor conhecido é a melhor descrição da hora).
   */
  imputedMode?: "gap" | "carry";
}

export function InsightsLineChart({
  title,
  points,
  range,
  formatValue = (v) => v.toFixed(1),
  color = "#34d399",
  imputedMode = "gap",
}: InsightsLineChartProps) {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const drawable = imputedMode === "carry" ? points : points.filter((p) => !p.is_imputed);

  if (points.length === 0 || drawable.length === 0) {
    return (
      <div className="flex h-56 flex-col rounded-lg border border-ink-700 bg-ink-900 p-4">
        <p className="mb-2 text-xs font-medium text-ink-300">{title}</p>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-ink-500">Sem dados para o período</p>
        </div>
      </div>
    );
  }

  // O eixo X cobre o período inteiro (inclusive horas sem dado); a escala Y
  // considera só o que será desenhado, para zero imputado não achatar a curva.
  const times = points.map((p) => new Date(p.bucket_start).getTime());
  const values = drawable.map((p) => p.value);
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);

  const rangeT = maxT - minT || 1;
  const rangeV = maxV - minV || 1;

  const toX = (t: number) => PAD_LEFT + ((t - minT) / rangeT) * INNER_W;
  const toY = (v: number) => PAD_TOP + INNER_H - ((v - minV) / rangeV) * INNER_H;

  // Em modo "gap", cada sequência contígua de pontos reais vira um segmento;
  // buracos imputados quebram a linha.
  const segments: InsightsTimeseriesPoint[][] = [];
  {
    let current: InsightsTimeseriesPoint[] = [];
    for (const p of points) {
      if (imputedMode === "carry" || !p.is_imputed) {
        current.push(p);
      } else if (current.length > 0) {
        segments.push(current);
        current = [];
      }
    }
    if (current.length > 0) segments.push(current);
  }
  const segmentPolylines = segments.map((segment) =>
    segment
      .map((p) => `${toX(new Date(p.bucket_start).getTime())},${toY(p.value)}`)
      .join(" "),
  );

  const yTicks = 4;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = minV + (rangeV * i) / yTicks;
    return { v, y: toY(v) };
  });

  // Hover na área inteira do gráfico: o alvo de 3px da bolinha era quase
  // impossível de acertar, e o tooltip renderizado sob o cursor roubava o
  // mouse e "piscava". Aqui o mouse em qualquer lugar seleciona o ponto
  // desenhável mais próximo no eixo X; o tooltip nunca captura eventos.
  function handleMouseMove(event: React.MouseEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const xInChart = ((event.clientX - rect.left) / rect.width) * CHART_W;
    let best: InsightsTimeseriesPoint | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const p of drawable) {
      const px = toX(new Date(p.bucket_start).getTime());
      const distance = Math.abs(px - xInChart);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = p;
      }
    }
    if (best === null) return;
    setTooltip({
      x: toX(new Date(best.bucket_start).getTime()),
      y: toY(best.value),
      point: best,
      formattedValue: formatValue(best.value),
    });
  }

  const xTickCount = Math.min(points.length, range === "24h" ? 6 : 7);
  const xStep = Math.max(1, Math.floor(points.length / xTickCount));
  const xLabels = points
    .filter((_, i) => i % xStep === 0 || i === points.length - 1)
    .map((p) => ({ label: formatXLabel(p.bucket_start, range), x: toX(new Date(p.bucket_start).getTime()) }));

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900 p-4">
      <p className="mb-2 text-xs font-medium text-ink-300">{title}</p>
      <div className="relative w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          width="100%"
          aria-label={title}
          style={{ display: "block" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltip(null)}
        >
          {/* Grid lines */}
          {yLabels.map(({ y }, i) => (
            <line
              key={i}
              x1={PAD_LEFT}
              y1={y}
              x2={CHART_W - PAD_RIGHT}
              y2={y}
              stroke="#2a2f3a"
              strokeWidth="1"
            />
          ))}

          {/* Y axis labels */}
          {yLabels.map(({ v, y }, i) => (
            <text
              key={i}
              x={PAD_LEFT - 6}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {formatValue(v)}
            </text>
          ))}

          {/* X axis labels */}
          {xLabels.map(({ label, x }, i) => (
            <text
              key={i}
              x={x}
              y={CHART_H - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {label}
            </text>
          ))}

          {/* Main line (um segmento por trecho contíguo de dados reais) */}
          {segmentPolylines.map((segment, i) => (
            <polyline
              key={i}
              points={segment}
              fill="none"
              stroke={color}
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Data points (visuais; o hover é da área do gráfico) */}
          {drawable.map((p, i) => {
            const px = toX(new Date(p.bucket_start).getTime());
            const py = toY(p.value);
            const isHovered = tooltip?.point === p;
            return (
              <circle
                key={i}
                cx={px}
                cy={py}
                r={isHovered ? 4.5 : 3}
                fill={p.is_imputed ? "#1f2937" : color}
                stroke={color}
                strokeWidth={p.is_imputed || isHovered ? "1" : "0"}
                strokeDasharray={p.is_imputed ? "2 2" : undefined}
                opacity={p.is_imputed && !isHovered ? 0.5 : 1}
                pointerEvents="none"
              />
            );
          })}

          {/* Tooltip */}
          {tooltip && (() => {
            const tipW = 140;
            const tipH = 44;
            const tipX = Math.min(tooltip.x + 8, CHART_W - PAD_RIGHT - tipW);
            const tipY = Math.max(tooltip.y - tipH - 6, PAD_TOP);
            const ts = new Date(tooltip.point.bucket_start);
            const tsLabel =
              range === "24h"
                ? ts.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
                : ts.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
            return (
              <g pointerEvents="none">
                <line
                  x1={tooltip.x}
                  y1={PAD_TOP}
                  x2={tooltip.x}
                  y2={CHART_H - PAD_BOTTOM}
                  stroke="#4b5563"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                <rect
                  x={tipX}
                  y={tipY}
                  width={tipW}
                  height={tipH}
                  rx="4"
                  fill="#1a1f2b"
                  stroke="#374151"
                  strokeWidth="1"
                />
                <text x={tipX + 8} y={tipY + 14} fontSize="10" fill="#9ca3af">
                  {tsLabel}
                  {tooltip.point.is_imputed ? " (estimado)" : ""}
                </text>
                <text x={tipX + 8} y={tipY + 32} fontSize="12" fill="#f9fafb" fontWeight="600">
                  {tooltip.formattedValue}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}
